import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Benutzer } from '@beholder/shared';
import type { MindmapKnoten, MindmapSpalte, SpaltenWert } from '@beholder/shared/module/mindmap';
import { Symbol } from '../../core/symbole';
import { Eingabe, Knopf } from '../../core/ui';
import { nachkommen, reihenfolge, vorfahren } from './baum';
import { FARBTON, KLAMMER_PFAD, PfadBild, personFarbe, SymbolBild } from './symbole';
import { neueId, type KartenZugriff } from './useKarte';
import { WertAnzeige, WertEingabe, wertText, type Personen } from './werte';

export interface Befehl {
  art: 'kind' | 'geschwister';
  id: string;
  nr: number;
}

interface Entwurf {
  id: string;
  elternId: string;
  index: number;
  text: string;
}

type Schluessel = 'titel' | 'beschreibung' | string;

export function TabellenAnsicht({
  z,
  zu,
  umschalten,
  setzeZu,
  auswahl,
  waehlen,
  darfSchreiben,
  personen,
  ich,
  loeschenFragen,
  befehl,
  spalteBearbeiten,
}: {
  z: KartenZugriff;
  zu: Set<string>;
  umschalten(id: string, an?: boolean): void;
  setzeZu(s: Set<string>): void;
  auswahl: string | null;
  waehlen(id: string | null, oeffnen?: boolean): void;
  darfSchreiben: boolean;
  personen: Personen;
  ich: Benutzer;
  loeschenFragen(id: string): void;
  befehl: Befehl | null;
  spalteBearbeiten(sp: MindmapSpalte | null): void;
}) {
  const rahmen = useRef<HTMLDivElement>(null);
  const [spalte, setSpalte] = useState<Schluessel>('titel');
  const [edit, setEdit] = useState<{ id: string; spalte: Schluessel; start?: string } | null>(null);
  const [entwurf, setEntwurfState] = useState<Entwurf | null>(null);
  const entwurfRef = useRef<Entwurf | null>(null);
  const setEntwurf = (e: Entwurf | null) => {
    entwurfRef.current = e;
    setEntwurfState(e);
  };
  const [filter, setFilter] = useState('');
  const baum = z.baum;

  const schluessel: Schluessel[] = useMemo(() => ['titel', 'beschreibung', ...z.spalten.map((s) => s.id)], [z.spalten]);

  // Sichtbare Zeilen (bei Suche: Treffer samt Vorfahren, ohne Einklappen)
  const zeilen = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return reihenfolge(baum, zu);
    const treffer = new Set<string>();
    for (const k of reihenfolge(baum)) {
      const text = [k.titel, k.beschreibung, ...z.spalten.map((s) => wertText(s, k.werte[s.id], personen))].join(' ').toLowerCase();
      if (text.includes(q)) {
        treffer.add(k.id);
        for (const v of vorfahren(baum, k.id)) treffer.add(v.id);
      }
    }
    return reihenfolge(baum).filter((k) => treffer.has(k.id));
  }, [baum, zu, filter, z.spalten, personen]);

  // Entwurfszeile an der richtigen Stelle einsortieren
  const anzeige = useMemo(() => {
    const liste: (MindmapKnoten | Entwurf)[] = [...zeilen];
    if (!entwurf) return liste;
    const geschw = baum.kinder.get(entwurf.elternId) ?? [];
    let nach = entwurf.elternId;
    if (entwurf.index > 0 && geschw[entwurf.index - 1]) {
      const vor = geschw[entwurf.index - 1]!;
      const unter = [vor.id, ...nachkommen(baum, vor.id)];
      const sichtbar = liste.filter((x) => unter.includes(x.id));
      nach = sichtbar[sichtbar.length - 1]?.id ?? vor.id;
    }
    const i = liste.findIndex((x) => x.id === nach);
    liste.splice(i + 1, 0, entwurf);
    return liste;
  }, [zeilen, entwurf, baum]);

  useEffect(() => {
    if (!befehl || !darfSchreiben) return;
    const k = z.knoten[befehl.id];
    if (!k) return;
    if (befehl.art === 'kind') neueZeile(k.id, (baum.kinder.get(k.id) ?? []).length);
    else geschwisterZeile(k.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [befehl?.nr]);

  const fokusRahmen = () => rahmen.current?.focus({ preventScroll: true });

  function neueZeile(elternId: string, index: number) {
    if (!darfSchreiben) return;
    if (zu.has(elternId)) umschalten(elternId, false);
    setEdit(null);
    setEntwurf({ id: neueId(), elternId, index, text: '' });
  }
  function geschwisterZeile(id: string) {
    const k = z.knoten[id];
    if (!k) return;
    if (!k.elternId) return neueZeile(id, 0);
    const l = baum.kinder.get(k.elternId) ?? [];
    neueZeile(k.elternId, l.findIndex((x) => x.id === id) + 1);
  }

  function entwurfFertig(weiter?: 'kind' | 'geschwister') {
    const e = entwurfRef.current;
    if (!e) return;
    entwurfRef.current = null;
    setEntwurf(null);
    const t = e.text.trim();
    if (!t) return fokusRahmen();
    void z.anlegen(e.elternId, e.index, t, {}, e.id);
    waehlen(e.id);
    setSpalte('titel');
    if (weiter === 'geschwister') setEntwurf({ id: neueId(), elternId: e.elternId, index: e.index + 1, text: '' });
    else if (weiter === 'kind') setEntwurf({ id: neueId(), elternId: e.id, index: 0, text: '' });
    else fokusRahmen();
  }

  function einruecken(id: string) {
    const k = z.knoten[id];
    if (!k?.elternId) return;
    const l = baum.kinder.get(k.elternId) ?? [];
    const i = l.findIndex((x) => x.id === id);
    const vor = l[i - 1];
    if (!vor) return;
    if (zu.has(vor.id)) umschalten(vor.id, false);
    void z.verschieben(id, vor.id, (baum.kinder.get(vor.id) ?? []).length);
  }
  function ausruecken(id: string) {
    const k = z.knoten[id];
    const e = k?.elternId ? z.knoten[k.elternId] : undefined;
    if (!e?.elternId) return;
    const l = baum.kinder.get(e.elternId) ?? [];
    void z.verschieben(id, e.elternId, l.findIndex((x) => x.id === e.id) + 1);
  }

  function speichern(id: string, s: Schluessel, neu: SpaltenWert) {
    const k = z.knoten[id];
    if (!k) return;
    if (s === 'titel') {
      const t = String(neu ?? '').trim();
      if (t && t !== k.titel) void z.aendern(id, { titel: t }, { titel: k.titel });
    } else if (s === 'beschreibung') {
      const t = String(neu ?? '');
      if (t !== k.beschreibung) void z.aendern(id, { beschreibung: t }, { beschreibung: k.beschreibung });
    } else {
      const alt = k.werte[s] ?? null;
      if (neu !== alt) void z.aendern(id, { werte: { [s]: neu } }, { werte: { [s]: alt } });
    }
  }

  function gehe(dz: number, ds: number) {
    const i = zeilen.findIndex((k) => k.id === auswahl);
    const j = Math.max(0, Math.min(zeilen.length - 1, (i < 0 ? 0 : i) + dz));
    const si = Math.max(0, Math.min(schluessel.length - 1, schluessel.indexOf(spalte) + ds));
    const ziel = zeilen[j];
    if (ziel) {
      waehlen(ziel.id);
      setSpalte(schluessel[si]!);
      document.getElementById(`mm-zeile-${ziel.id}`)?.scrollIntoView({ block: 'nearest' });
    }
  }

  function editFertig(id: string, s: Schluessel, neu: SpaltenWert, weiter?: 'runter' | 'rechts' | 'links') {
    speichern(id, s, neu);
    setEdit(null);
    fokusRahmen();
    if (weiter === 'runter') gehe(1, 0);
    else if (weiter === 'rechts') gehe(0, 1);
    else if (weiter === 'links') gehe(0, -1);
  }

  const sp = (s: Schluessel) => z.spalten.find((x) => x.id === s);

  function taste(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (edit || entwurfRef.current || e.target !== rahmen.current) return;
    const id = auswahl;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        return gehe(1, 0);
      case 'ArrowUp':
        e.preventDefault();
        return gehe(-1, 0);
      case 'ArrowRight':
        e.preventDefault();
        return gehe(0, 1);
      case 'ArrowLeft':
        e.preventDefault();
        return gehe(0, -1);
    }
    if (!id) return;
    const k = z.knoten[id];
    if (!k) return;
    switch (e.key) {
      case 'Enter':
      case 'F2':
        e.preventDefault();
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) return geschwisterZeile(id);
        if (!darfSchreiben) return waehlen(id, true);
        if (sp(spalte)?.typ === 'haken') return speichern(id, spalte, !(k.werte[spalte] === true));
        return setEdit({ id, spalte });
      case 'Insert':
        e.preventDefault();
        return geschwisterZeile(id);
      case 'Tab':
        e.preventDefault();
        if (!darfSchreiben) return;
        return e.shiftKey ? ausruecken(id) : einruecken(id);
      case 'Delete':
      case 'Backspace':
        e.preventDefault();
        if (!darfSchreiben) return;
        if (spalte === 'titel') return k.elternId ? loeschenFragen(id) : undefined;
        return speichern(id, spalte, spalte === 'beschreibung' ? '' : null);
      case ' ':
        if (sp(spalte)?.typ === 'haken' && darfSchreiben) {
          e.preventDefault();
          return speichern(id, spalte, !(k.werte[spalte] === true));
        }
        if (spalte === 'titel' && (baum.kinder.get(id) ?? []).length) {
          e.preventDefault();
          return umschalten(id);
        }
        break;
      case 'Escape':
        return waehlen(null);
    }
    const art = spalte === 'titel' || spalte === 'beschreibung' ? 'text' : sp(spalte)?.typ;
    if (darfSchreiben && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && (art === 'text' || art === 'zahl' || art === 'betrag' || art === 'link' || art === 'fortschritt')) {
      e.preventDefault();
      setEdit({ id, spalte, start: e.key });
    }
  }

  const summen = z.spalten
    .filter((s) => s.typ === 'zahl' || s.typ === 'betrag' || s.typ === 'fortschritt')
    .map((s) => {
      const zahlen = zeilen.map((k) => k.werte[s.id]).filter((w): w is number => typeof w === 'number');
      const summe = zahlen.reduce((a, w) => a + w, 0);
      // Fortschritt: Durchschnitt der ausgefüllten Zeilen statt Summe
      return { s, summe: s.typ === 'fortschritt' ? (zahlen.length ? Math.round(summe / zahlen.length) : null) : summe };
    });
  const andere = z.anwesend.filter((p) => p.benutzerId !== ich.id && p.knotenId);
  const alleZu = [...baum.kinder.keys()].filter((id) => id !== baum.wurzel?.id);

  return (
    <div className="mm-tabelle-seite">
      <div className="mm-tabelle-leiste">
        <Eingabe type="search" placeholder="In der Tabelle suchen …" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Suchen" />
        <Knopf klein art="leise" onClick={() => setzeZu(new Set())} title="Alle Äste aufklappen">
          Alle auf
        </Knopf>
        <Knopf klein art="leise" onClick={() => setzeZu(new Set(alleZu))} title="Alle Äste einklappen">
          Alle zu
        </Knopf>
        <span className="abstand" />
        {darfSchreiben && (
          <>
            <Knopf klein onClick={() => (auswahl ? geschwisterZeile(auswahl) : baum.wurzel && neueZeile(baum.wurzel.id, (baum.kinder.get(baum.wurzel.id) ?? []).length))}>
              {Symbol.plus} Zeile
            </Knopf>
            <Knopf klein onClick={() => spalteBearbeiten(null)}>
              {Symbol.plus} Spalte
            </Knopf>
          </>
        )}
      </div>

      <div ref={rahmen} className="mm-tabelle-rahmen" tabIndex={0} onKeyDown={taste} aria-label="Tabelle der Mindmap">
        <table className="mm-tabelle">
          <thead>
            <tr>
              <th className="mm-sp-nr">Nr.</th>
              <th className="mm-sp-titel">Titel</th>
              <th className="mm-sp-text">Beschreibung</th>
              {z.spalten.map((s) => (
                <th key={s.id} className={`mm-sp-wert mm-sp-${s.typ}`}>
                  {darfSchreiben ? (
                    <button type="button" className="mm-kopf-knopf" onClick={() => spalteBearbeiten(s)} title="Spalte bearbeiten">
                      {s.name}
                    </button>
                  ) : (
                    s.name
                  )}
                </th>
              ))}
              <th className="mm-sp-anlagen" title="Anlagen">
                <PfadBild d={KLAMMER_PFAD} groesse={15} />
              </th>
              {darfSchreiben && <th className="mm-sp-aktion" aria-label="Aktionen" />}
            </tr>
          </thead>
          <tbody>
            {anzeige.map((x) => {
              if (!('version' in x)) {
                const ebene = (baum.ebene.get(x.elternId) ?? 0) + 1;
                return (
                  <tr key={x.id} className="mm-zeile mm-entwurf">
                    <td className="mm-sp-nr leise">neu</td>
                    <td className="mm-sp-titel">
                      <div className="mm-titel-zelle" style={{ paddingLeft: ebene * 18 }}>
                        <input
                          className="eingabe mm-wert-eingabe"
                          autoFocus
                          value={x.text}
                          placeholder="Titel der neuen Zeile …"
                          maxLength={500}
                          onChange={(e) => setEntwurf({ ...x, text: e.target.value })}
                          onBlur={() => setTimeout(() => entwurfRef.current?.id === x.id && entwurfFertig())}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              entwurfFertig('geschwister');
                            } else if (e.key === 'Tab') {
                              e.preventDefault();
                              entwurfFertig('kind');
                            } else if (e.key === 'Escape') {
                              setEntwurf(null);
                              fokusRahmen();
                            }
                          }}
                        />
                      </div>
                    </td>
                    <td colSpan={z.spalten.length + 2 + (darfSchreiben ? 1 : 0)} className="leise klein">
                      Enter = speichern und nächste Zeile · Tab = Unterpunkt · Esc = abbrechen
                    </td>
                  </tr>
                );
              }
              const k = x;
              const ebene = baum.ebene.get(k.id) ?? 0;
              const kinder = baum.kinder.get(k.id)?.length ?? 0;
              const istAus = auswahl === k.id;
              const f = baum.astFarbe.get(k.id);
              const person = andere.find((p) => p.knotenId === k.id);
              const zelle = (s: Schluessel) => `${istAus && spalte === s ? ' mm-zelle-aus' : ''}`;
              const klick = (s: Schluessel) => () => {
                waehlen(k.id);
                setSpalte(s);
              };
              const doppel = (s: Schluessel) => () => darfSchreiben && setEdit({ id: k.id, spalte: s });
              return (
                <tr
                  key={k.id}
                  id={`mm-zeile-${k.id}`}
                  className={`mm-zeile${istAus ? ' mm-zeile-aus' : ''}${ebene === 0 ? ' mm-zeile-wurzel' : ''}`}
                  style={person ? { boxShadow: `inset 3px 0 0 ${personFarbe(person.benutzerId)}` } : undefined}
                >
                  <td className="mm-sp-nr leise" onClick={klick('titel')}>
                    {baum.nummer.get(k.id)}
                  </td>
                  <td className={`mm-sp-titel${zelle('titel')}`} onClick={klick('titel')} onDoubleClick={doppel('titel')}>
                    <div className="mm-titel-zelle" style={{ paddingLeft: ebene * 18 }}>
                      {kinder > 0 && ebene > 0 ? (
                        <button
                          type="button"
                          className="mm-klapp-knopf"
                          aria-label={zu.has(k.id) ? 'Ausklappen' : 'Einklappen'}
                          onClick={(e) => {
                            e.stopPropagation();
                            umschalten(k.id);
                          }}
                        >
                          {zu.has(k.id) && !filter ? '▸' : '▾'}
                        </button>
                      ) : (
                        <span className="mm-klapp-platz" />
                      )}
                      <span className="mm-punkt" style={{ background: k.farbe ? FARBTON[k.farbe] : f ? FARBTON[f] : 'var(--akzent)' }} />
                      {k.symbol && (
                        <span className="mm-zeilen-symbol" style={{ color: f ? FARBTON[f] : undefined }}>
                          <SymbolBild symbol={k.symbol} groesse={15} />
                        </span>
                      )}
                      {edit?.id === k.id && edit.spalte === 'titel' ? (
                        <TextEdit
                          start={edit.start ?? k.titel}
                          fertig={(t, w) => editFertig(k.id, 'titel', t, w)}
                          abbrechen={() => {
                            setEdit(null);
                            fokusRahmen();
                          }}
                        />
                      ) : (
                        <span className="mm-titel-text">{k.titel}</span>
                      )}
                      {zu.has(k.id) && kinder > 0 && !filter && <span className="marke">+{nachkommen(baum, k.id).length}</span>}
                      {person && (
                        <span className="mm-person-marke" style={{ background: personFarbe(person.benutzerId) }}>
                          {person.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`mm-sp-text${zelle('beschreibung')}`} onClick={klick('beschreibung')} onDoubleClick={doppel('beschreibung')}>
                    {edit?.id === k.id && edit.spalte === 'beschreibung' ? (
                      <TextEdit
                        mehrzeilig
                        start={edit.start ?? k.beschreibung}
                        fertig={(t, w) => editFertig(k.id, 'beschreibung', t, w)}
                        abbrechen={() => {
                          setEdit(null);
                          fokusRahmen();
                        }}
                      />
                    ) : (
                      <span className="mm-beschreibung-kurz">{k.beschreibung}</span>
                    )}
                  </td>
                  {z.spalten.map((s) => (
                    <td
                      key={s.id}
                      className={`mm-sp-wert mm-sp-${s.typ}${zelle(s.id)}`}
                      onClick={() => {
                        klick(s.id)();
                        if (s.typ === 'haken' && darfSchreiben && istAus && spalte === s.id) speichern(k.id, s.id, !(k.werte[s.id] === true));
                      }}
                      onDoubleClick={() => s.typ !== 'haken' && doppel(s.id)()}
                    >
                      {edit?.id === k.id && edit.spalte === s.id ? (
                        <WertEingabe
                          sp={s}
                          wert={k.werte[s.id]}
                          personen={personen}
                          autoFocus
                          tasteWeiter={edit.start}
                          fertig={(neu, w) => editFertig(k.id, s.id, neu, w)}
                          abbrechen={() => {
                            setEdit(null);
                            fokusRahmen();
                          }}
                        />
                      ) : (
                        <WertAnzeige sp={s} w={k.werte[s.id]} personen={personen} />
                      )}
                    </td>
                  ))}
                  <td className="mm-sp-anlagen">
                    {k.anlagen > 0 && (
                      <button type="button" className="mm-anlagen-knopf" onClick={() => waehlen(k.id, true)} title="Anlagen ansehen">
                        <PfadBild d={KLAMMER_PFAD} groesse={14} /> {k.anlagen}
                      </button>
                    )}
                  </td>
                  {darfSchreiben && (
                    <td className="mm-sp-aktion">
                      <div className="mm-zeilen-aktionen">
                        <button type="button" title="Ausrücken (Umschalt+Tab)" aria-label="Ausrücken" disabled={ebene < 2} onClick={() => ausruecken(k.id)}>
                          ⇤
                        </button>
                        <button type="button" title="Einrücken (Tab)" aria-label="Einrücken" disabled={ebene < 1} onClick={() => einruecken(k.id)}>
                          ⇥
                        </button>
                        <button type="button" title="Neue Zeile darunter (Strg+Enter)" aria-label="Neue Zeile darunter" onClick={() => geschwisterZeile(k.id)}>
                          +
                        </button>
                        <button type="button" title="Öffnen" aria-label="Öffnen" onClick={() => waehlen(k.id, true)}>
                          ⋯
                        </button>
                        {ebene > 0 && (
                          <button type="button" title="Löschen" aria-label="Löschen" className="mm-gefahr" onClick={() => loeschenFragen(k.id)}>
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {summen.length > 0 && (
            <tfoot>
              <tr>
                <td />
                <td className="leise">Summe ({zeilen.length} Zeilen)</td>
                <td />
                {z.spalten.map((s) => {
                  const x = summen.find((y) => y.s.id === s.id);
                  return (
                    <td key={s.id} className={`mm-sp-wert mm-sp-${s.typ}`}>
                      {x && x.summe !== null ? `${s.typ === 'fortschritt' ? 'Ø ' : ''}${wertText(s, x.summe, personen)}` : ''}
                    </td>
                  );
                })}
                <td />
                {darfSchreiben && <td />}
              </tr>
            </tfoot>
          )}
        </table>
        {zeilen.length === 0 && filter && <p className="leise mm-leer-hinweis">Nichts gefunden.</p>}
      </div>
      <p className="leise klein mm-tasten-hinweis">
        {darfSchreiben
          ? 'Pfeiltasten wandern · Enter/F2 bearbeiten · Tippen überschreibt · Tab einrücken, Umschalt+Tab ausrücken · Strg+Enter neue Zeile · Entf leert die Zelle (in „Titel“: Zeile löschen)'
          : 'Nur ansehen – zum Bearbeiten fehlt das Schreibrecht.'}
      </p>
    </div>
  );
}

function TextEdit({
  start,
  mehrzeilig,
  fertig,
  abbrechen,
}: {
  start: string;
  mehrzeilig?: boolean;
  fertig(t: string, weiter?: 'runter' | 'rechts' | 'links'): void;
  abbrechen(): void;
}) {
  const [t, setT] = useState(start);
  const erledigt = useRef(false);
  const ende = (w?: 'runter' | 'rechts' | 'links') => {
    if (erledigt.current) return;
    erledigt.current = true;
    fertig(t, w);
  };
  const tasten = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      erledigt.current = true;
      abbrechen();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ende('runter');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      ende(e.shiftKey ? 'links' : 'rechts');
    }
  };
  const gemeinsam = {
    className: 'eingabe mm-wert-eingabe',
    autoFocus: true,
    value: t,
    onKeyDown: tasten,
    onBlur: () => ende(),
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const l = e.target.value.length;
      e.target.setSelectionRange(l, l);
    },
  };
  return mehrzeilig ? (
    <textarea {...gemeinsam} rows={3} onChange={(e) => setT(e.target.value)} />
  ) : (
    <input {...gemeinsam} maxLength={500} onChange={(e) => setT(e.target.value)} />
  );
}
